import React, {useEffect, useState} from 'react';
import {Button, useNotify, useRecordContext, useShowContext,} from 'react-admin';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Chip, CircularProgress,
    Divider,
    Grid,
    IconButton,
    Link as MuiLink,
    Paper,
    TextField as MUITextField,
    Typography
} from '@material-ui/core';
import {Link} from 'react-router-dom';
import {linkToRecord} from 'ra-core';
import CommentIcon from '@material-ui/icons/Comment';
import SendIcon from '@material-ui/icons/Send';
import {makeStyles} from '@material-ui/core/styles';
import {client, clientNoJson} from "../../dataProvider";
import {AssignedField} from './AssignedField';
import {CreateReminder} from "./CreateReminder";
import {BiTrash} from "react-icons/bi";
import {getUser} from "../../authProvider";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import AttachFileIcon from '@material-ui/icons/AttachFile';
import {getFileNameFromContentDisposition} from "../../shared/utils";



const useStyles = makeStyles(theme => ({
    card: {
        marginBottom: theme.spacing(2)
    },
    attachmentsBox: {
        marginTop: theme.spacing(1.5),
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
    },
    attachmentChip: {
        maxWidth: 260,
    },
    hiddenFileInput: {
        display: "none",
    },
    commentActions: {
        display: "flex",
        alignItems: "center",
        gap: 4,
    },
    uploadHint: {
        marginTop: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
    commentSection: {
        marginTop: theme.spacing(3)
    },
    commentCard: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2)
    },
    commentHeader: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(1)
    },
    commentAvatar: {
        marginRight: theme.spacing(1),
        backgroundColor: theme.palette.primary.main
    },
    commentForm: {
        marginTop: theme.spacing(2)
    },
    commentInput: {
        marginRight: theme.spacing(1)
    },
    chip: {
        margin: theme.spacing(0.5)
    },
    statusChips: {
        display: 'flex',
        flexWrap: 'wrap',
        marginBottom: theme.spacing(2)
    },
    divider: {
        margin: theme.spacing(2, 0)
    },
    section: {
        marginBottom: theme.spacing(3)
    },
    sectionTitle: {
        marginBottom: theme.spacing(1)
    }
}));


interface CommentAttachment {
    id: string;
    name: string;
    url: string;
    size?: number;
    uploaded_at?: string;
}

interface Comment {
    id: string;
    user: {
        id: string;
        username: string;
        last_name: string;
        first_name: string;
    };
    date: string;
    comment: string;
    attachments?: CommentAttachment[];
}

interface Comment {
    id: string;
    user: {
        id: string;
        username: string;
        last_name: string;
        first_name: string;
    };
    date: string;
    comment: string;
}


async function uploadCommentAttachments(commentId: string, files: File[]) {
    const formData = new FormData();

    files.forEach((file) => {
        formData.append("files", file);
    });

    const response = await clientNoJson(
        `/api/payment_schedule_comments/${commentId}/attachments/`,
        {
            method: "POST",
            data: formData,
            headers: {
                "Content-Type": "multipart/form-data",
            },
        }
    );

    return response.data;
}

async function deleteCommentAttachment(commentId: string, attachmentId: string) {
    await clientNoJson(
        `/api/payment_schedule_comments/${commentId}/attachments/${attachmentId}`,
        {
            method: "DELETE",
        }
    );

    return true;
}


export const downloadCommentAttachment = async (attachmentId: string) => {
    let res = null;
    try {
        res = await clientNoJson(`/api/payment_schedule_comments/attachments/${attachmentId}/download/`, {
            responseType: "arraybuffer",
            method: "GET",
        });
    } catch (err) {
        return;
    }

    if (res) {
        const data = res.data;
        const url = window.URL.createObjectURL(
            new Blob([data], {
                type: res.headers["content-type"],
            })
        );
        const actualFileName = getFileNameFromContentDisposition(
            res.headers["content-disposition"]
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", actualFileName);
        document.body.appendChild(link);
        link.click();
        if (link.parentNode) link.parentNode.removeChild(link);
    }
};

const CommentAttachments = ({
                                comment,
                                isOwner,
                                onAttachmentDeleted,
                            }: {
    comment: Comment;
    isOwner: boolean;
    onAttachmentDeleted: (commentId: string, attachmentId: string) => void;
}) => {
    const classes = useStyles();
    const notify = useNotify();
    const handleDownload = async (attachment: CommentAttachment) => {
        try {
            await downloadCommentAttachment(attachment.id);
        } catch {
            notify("Errore durante il download", { type: "error" });
        }
    };
    const attachments = comment.attachments || [];

    if (attachments.length === 0) return null;

    const handleDeleteAttachment = async (attachmentId: string) => {
        try {
            await deleteCommentAttachment(comment.id, attachmentId);
            onAttachmentDeleted(comment.id, attachmentId);
            notify("Allegato eliminato");
        } catch {
            notify("Errore durante l'eliminazione dell'allegato", {
                type: "error",
            });
        }
    };

    return (
        <Box className={classes.attachmentsBox}>
            {attachments.map((attachment) => (
                <Chip
                    key={attachment.id}
                    className={classes.attachmentChip}
                    icon={<AttachFileIcon />}
                    label={attachment.size ? `${attachment.name} · ${attachment.size} MB` : attachment.name}
                    clickable
                    onClick={() => handleDownload(attachment)}
                    onDelete={
                        isOwner
                            ? (event: any) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleDeleteAttachment(attachment.id);
                            }
                            : undefined
                    }
                />
            ))}
        </Box>
    );
};

const AttachFilesButton = ({
                               commentId,
                               onUploaded,
                           }: {
    commentId: string;
    onUploaded: (commentId: string, attachments: CommentAttachment[]) => void;
}) => {
    const classes = useStyles();
    const notify = useNotify();
    const inputId = `comment-attachment-${commentId}`;
    const [uploading, setUploading] = useState(false);

    const handleFilesChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);

        if (files.length === 0) return;

        try {
            setUploading(true);
            const uploadedAttachments = await uploadCommentAttachments(commentId, files);

            onUploaded(commentId, uploadedAttachments);

            notify(
                files.length === 1
                    ? "Allegato caricato"
                    : "Allegati caricati"
            );
        } catch {
            notify("Errore durante il caricamento degli allegati", {
                type: "error",
            });
        } finally {
            setUploading(false);
            event.target.value = "";
        }
    };

    return (
        <>
            <input
                id={inputId}
                className={classes.hiddenFileInput}
                type="file"
                multiple
                onChange={handleFilesChange}
            />

            <label htmlFor={inputId}>
                <IconButton
                    size="small"
                    component="span"
                    disabled={uploading}
                    title="Allega file"
                >
                    {uploading ? (
                        <CircularProgress size={18} />
                    ) : (
                        <AttachFileIcon fontSize="small" />
                    )}
                </IconButton>
            </label>
        </>
    );
};


const CommentList = ({
                         comments,
                         onDelete,
                         updateComment,
                         onAttachmentsUploaded,
                         onAttachmentDeleted,
                     }: any) => {
    const classes = useStyles();
    const user = getUser();

    const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
    const [draftComment, setDraftComment] = useState<string>("");

    const startEdit = (comment: Comment) => {
        setEditingCommentId(comment.id);
        setDraftComment(comment.comment);
    };

    const cancelEdit = () => {
        setEditingCommentId(null);
        setDraftComment("");
    };

    const onSave = (commentId: string) => {
        if (!draftComment.trim()) return;

        updateComment(commentId, draftComment);
        setEditingCommentId(null);
        setDraftComment("");
    };

    return (
        <Box>
            {comments?.map((item: Comment) => {
                const isOwner = item.user.username === user.username;
                const isEditing = editingCommentId === item.id;

                return (
                    <Paper
                        key={item.id}
                        className={classes.commentCard}
                        variant="outlined"
                        style={{
                            borderRadius: 10,
                            backgroundColor: "#fafafa",
                        }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box display="flex" alignItems="center">
                                <Avatar className={classes.commentAvatar}>
                                    {item.user.username.charAt(0).toUpperCase()}
                                </Avatar>

                                <Box ml={1}>
                                    <Typography variant="subtitle2">
                                        {item.user.username}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {new Date(item.date).toLocaleString()}
                                    </Typography>
                                </Box>
                            </Box>

                            {isOwner && (
                                <Box className={classes.commentActions}>
                                    {isEditing ? (
                                        <>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => onSave(item.id)}
                                                disabled={!draftComment.trim()}
                                            >
                                                <SaveIcon fontSize="small" />
                                            </IconButton>

                                            <Button
                                                size="small"
                                                onClick={cancelEdit}
                                                label="Annulla"
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <AttachFilesButton
                                                commentId={item.id}
                                                onUploaded={onAttachmentsUploaded}
                                            />

                                            <IconButton
                                                size="small"
                                                onClick={() => startEdit(item)}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>

                                            <IconButton
                                                size="small"
                                                onClick={() => onDelete(item.id)}
                                                style={{ color: "#d32f2f" }}
                                            >
                                                <BiTrash />
                                            </IconButton>
                                        </>
                                    )}
                                </Box>
                            )}
                        </Box>

                        <Box mt={2}>
                            {isEditing ? (
                                <MUITextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    variant="outlined"
                                    value={draftComment}
                                    onChange={(e) => setDraftComment(e.target.value)}
                                    autoFocus
                                />
                            ) : (
                                <Typography
                                    variant="body2"
                                    style={{
                                        whiteSpace: "pre-wrap",
                                        lineHeight: 1.6,
                                    }}
                                >
                                    {item.comment}
                                </Typography>
                            )}

                            {!isEditing && (
                                <CommentAttachments
                                    comment={item}
                                    isOwner={isOwner}
                                    onAttachmentDeleted={onAttachmentDeleted}
                                />
                            )}
                        </Box>
                    </Paper>
                );
            })}

            {comments?.length === 0 && (
                <Paper
                    variant="outlined"
                    style={{
                        padding: 24,
                        textAlign: "center",
                        borderRadius: 8,
                        backgroundColor: "#fafafa",
                    }}
                >
                    <Typography variant="body2" color="textSecondary">
                        Nessun commento presente.
                    </Typography>
                </Paper>
            )}
        </Box>
    );
};


const CommentForm = ({ recordId, updateComments }: any) => {
    const classes = useStyles();
    const [comment, setComment] = useState("");
    const [files, setFiles] = useState<File[]>([]);
    const [saving, setSaving] = useState(false);
    const notify = useNotify();

    const handleSubmit = async () => {
        if (!comment.trim()) return;

        try {
            setSaving(true);

            const newComment = await client("debt_collection/create_comment", {
                method: "POST",
                data: {
                    id: recordId,
                    comment: comment,
                },
            });

            let attachments: CommentAttachment[] = [];

            if (files.length > 0) {
                attachments = await uploadCommentAttachments(newComment.id, files);
            }

            updateComments({
                ...newComment,
                attachments,
            });

            notify("Commento aggiunto con successo");
            setComment("");
            setFiles([]);
        } catch {
            notify("Errore durante l'aggiunta del commento", {
                type: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFiles(Array.from(event.target.files || []));
    };

    const removeSelectedFile = (fileName: string) => {
        setFiles((prev) => prev.filter((file) => file.name !== fileName));
    };

    return (
        <Box className={classes.commentForm}>
            <Box display="flex" alignItems="flex-start">
                <MUITextField
                    className={classes.commentInput}
                    label="Aggiungi un commento"
                    multiline
                    rows={3}
                    fullWidth
                    variant="outlined"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                />

                <IconButton
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!comment.trim() || saving}
                >
                    {saving ? <CircularProgress size={22} /> : <SendIcon />}
                </IconButton>
            </Box>

            <Box mt={1} display="flex" alignItems="center">
                <input
                    id="new-comment-files"
                    className={classes.hiddenFileInput}
                    type="file"
                    multiple
                    onChange={handleFilesChange}
                />

                <label htmlFor="new-comment-files">
                    <Button
                        component="span"
                        size="small"
                        label="Allega file"
                    >
                        <AttachFileIcon fontSize="small" />
                    </Button>
                </label>

                {files.length > 0 && (
                    <Typography
                        variant="caption"
                        className={classes.uploadHint}
                        style={{ marginLeft: 8 }}
                    >
                        {files.length === 1
                            ? "1 file selezionato"
                            : `${files.length} file selezionati`}
                    </Typography>
                )}
            </Box>

            {files.length > 0 && (
                <Box className={classes.attachmentsBox}>
                    {files.map((file) => (
                        <Chip
                            key={file.name}
                            icon={<AttachFileIcon />}
                            label={`${file.name} · ${(file.size / 1024 / 1024).toFixed(2)} MB`}
                            onDelete={() => removeSelectedFile(file.name)}
                            className={classes.attachmentChip}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};


export const DebtCollectionDetails = () => {
    const classes = useStyles();
    const record = useRecordContext();


    if (!record) return null;

    return (
        <Card className={classes.card}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h5">
                        Fattura #{record.sequence_number}
                    </Typography>
                    <AssignedField record={record}/>
                </Box>

                <div className={classes.statusChips}>
                    {record.status_display && (
                        <Chip
                            className={classes.chip}
                            label={record.status_display}
                            color="primary"
                        />
                    )}
                    {record.invoice_type_display && (
                        <Chip
                            className={classes.chip}
                            label={record.invoice_type_display}
                            variant="outlined"
                        />
                    )}
                    {record.type && (
                        <Chip
                            className={classes.chip}
                            label={record.type}
                            variant="outlined"
                        />
                    )}
                    {record.fuel && (
                        <Chip
                            className={classes.chip}
                            label="Carburante"
                            color="secondary"
                        />
                    )}
                </div>

                <Divider className={classes.divider}/>

                <Grid container spacing={3} className={classes.section}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" className={classes.sectionTitle}>
                            Azienda
                        </Typography>
                        {record.invoice.company_object && (
                            <MuiLink
                                component={Link}
                                to={linkToRecord("/subjects", record.invoice.company_object.company.id, "edit")}
                            >
                                <Typography variant="body1">{record.invoice.company_object.name}</Typography>
                            </MuiLink>
                        )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" className={classes.sectionTitle}>
                            Cliente
                        </Typography>
                        {record.invoice.client && (
                            <MuiLink
                                component={Link}
                                to={linkToRecord("/subjects", record.invoice.client.id, "edit")}
                            >
                                <Typography variant="body1">{record.invoice.client.business_name}</Typography>
                            </MuiLink>
                        )}
                    </Grid>
                </Grid>

                <Divider className={classes.divider}/>

                <Grid container spacing={3} className={classes.section}>
                    <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" color="textSecondary">Data fattura</Typography>
                        <Typography variant="body1">
                            {record.invoice.invoice_date && new Date(record.invoice.invoice_date).toLocaleDateString()}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" color="textSecondary">Scadenza</Typography>
                        <Typography variant="body1">
                            {record.expiration_date && new Date(record.expiration_date).toLocaleDateString()}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" color="textSecondary">Invio</Typography>
                        <Typography variant="body1">
                            {record.invoice.invoice_sent && new Date(record.invoice.invoice_sent).toLocaleString()}
                        </Typography>
                    </Grid>
                </Grid>

                <Grid container spacing={3} className={classes.section}>
                    <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" color="textSecondary">Imponibile</Typography>
                        <Typography variant="body1">{record.invoice.taxable} €</Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" color="textSecondary">Totale</Typography>
                        <Typography variant="body1">{record.amount} €</Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                        <Typography variant="body1">{record.email}</Typography>
                    </Grid>
                </Grid>

                <Box display="flex" alignItems="center" className={classes.section}>
                    <CreateReminder row={record}/>
                </Box>

                {record.invoice_reference && (
                    <Box className={classes.section}>
                        <Typography variant="subtitle2" color="textSecondary">Riferimento</Typography>
                        <Typography variant="body1">
                            N° {record.invoice_reference.type}-{record.invoice_reference.sequence_number}
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export const CommentSection = () => {
    const { record } = useShowContext();
    const classes = useStyles();
    const [comments, setComments] = useState<Comment[]>([]);
    const notify = useNotify();

    useEffect(() => {
        if (!record?.id) return;

        client(`debt_collection/${record.id}/get_comments`, {
            method: "GET",
        })
            .then((response) => {
                if (!response) {
                    setComments([]);
                    return;
                }

                setComments(response);
            })
            .catch((error) => {
                console.error("Failed to fetch comments:", error);
                setComments([]);
            });
    }, [record?.id]);

    const updateComments = (newComment: Comment) => {
        setComments((prev) => [...prev, newComment]);
    };

    const handleAttachmentsUploaded = (
        commentId: string,
        uploadedAttachments: CommentAttachment[]
    ) => {
        setComments((prev) =>
            prev.map((comment) =>
                comment.id === commentId
                    ? {
                        ...comment,
                        attachments: [
                            ...(comment.attachments || []),
                            ...uploadedAttachments,
                        ],
                    }
                    : comment
            )
        );
    };

    const handleAttachmentDeleted = (
        commentId: string,
        attachmentId: string
    ) => {
        setComments((prev) =>
            prev.map((comment) =>
                comment.id === commentId
                    ? {
                        ...comment,
                        attachments: (comment.attachments || []).filter(
                            (attachment) => attachment.id !== attachmentId
                        ),
                    }
                    : comment
            )
        );
    };

    const deleteComment = (commentId: string) => {
        client("debt_collection/delete_comment", {
            method: "POST",
            data: {
                id: commentId,
            },
        })
            .then(() => {
                setComments((prev) =>
                    prev.filter((comment) => comment.id !== commentId)
                );
                notify("Commento eliminato con successo");
            })
            .catch(() => {
                notify("Errore durante l'eliminazione del commento", {
                    type: "error",
                });
            });
    };

    const updateComment = (commentId: string, newComment: string) => {
        client("debt_collection/update_comment", {
            method: "POST",
            data: {
                id: commentId,
                comment: newComment,
            },
        })
            .then(() => {
                setComments((prevComments) =>
                    prevComments.map((item) =>
                        item.id === commentId
                            ? {
                                ...item,
                                comment: newComment,
                            }
                            : item
                    )
                );

                notify("Commento aggiornato con successo");
            })
            .catch(() => {
                notify("Errore durante l'aggiornamento", {
                    type: "error",
                });
            });
    };

    return (
        <div className={classes.commentSection}>
            <Box display="flex" alignItems="center" mb={2}>
                <CommentIcon color="primary" />
                <Typography variant="h6" style={{ marginLeft: 8 }}>
                    Commenti
                </Typography>
            </Box>

            <CommentList
                comments={comments}
                onDelete={deleteComment}
                updateComment={updateComment}
                onAttachmentsUploaded={handleAttachmentsUploaded}
                onAttachmentDeleted={handleAttachmentDeleted}
            />

            <CommentForm
                recordId={record?.id}
                updateComments={updateComments}
            />
        </div>
    );
};

export const CommentSectionDialog = ({ record }: { record: any }) => {
    const [comments, setComments] = useState<Comment[]>([]);
    const [loading, setLoading] = useState(false);
    const notify = useNotify();

    useEffect(() => {
        if (!record?.id) return;

        setLoading(true);

        client(`debt_collection/${record.id}/get_comments`, {
            method: "GET",
        })
            .then((response) => {
                setComments(response || []);
            })
            .catch(() => {
                setComments([]);
                notify("Errore durante il caricamento dei commenti", {
                    type: "error",
                });
            })
            .finally(() => {
                setLoading(false);
            });
    }, [record?.id, notify]);

    const updateComments = (newComment: Comment) => {
        setComments((prev) => [...prev, newComment]);
    };

    const handleAttachmentsUploaded = (
        commentId: string,
        uploadedAttachments: CommentAttachment[]
    ) => {
        setComments((prev) =>
            prev.map((comment) =>
                comment.id === commentId
                    ? {
                        ...comment,
                        attachments: [
                            ...(comment.attachments || []),
                            ...uploadedAttachments,
                        ],
                    }
                    : comment
            )
        );
    };

    const handleAttachmentDeleted = (
        commentId: string,
        attachmentId: string
    ) => {
        setComments((prev) =>
            prev.map((comment) =>
                comment.id === commentId
                    ? {
                        ...comment,
                        attachments: (comment.attachments || []).filter(
                            (attachment) => attachment.id !== attachmentId
                        ),
                    }
                    : comment
            )
        );
    };

    const deleteComment = (commentId: string) => {
        client("debt_collection/delete_comment", {
            method: "POST",
            data: {
                id: commentId,
            },
        })
            .then(() => {
                setComments((prev) =>
                    prev.filter((comment) => comment.id !== commentId)
                );
                notify("Commento eliminato con successo");
            })
            .catch(() => {
                notify("Errore durante l'eliminazione del commento", {
                    type: "error",
                });
            });
    };

    const updateComment = (commentId: string, newComment: string) => {
        client("debt_collection/update_comment", {
            method: "POST",
            data: {
                id: commentId,
                comment: newComment,
            },
        })
            .then(() => {
                setComments((prev) =>
                    prev.map((item) =>
                        item.id === commentId
                            ? {
                                ...item,
                                comment: newComment,
                            }
                            : item
                    )
                );

                notify("Commento aggiornato con successo");
            })
            .catch(() => {
                notify("Errore durante l'aggiornamento", {
                    type: "error",
                });
            });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    return (
        <Box>
            <CommentList
                comments={comments}
                onDelete={deleteComment}
                updateComment={updateComment}
                onAttachmentsUploaded={handleAttachmentsUploaded}
                onAttachmentDeleted={handleAttachmentDeleted}
            />

            <CommentForm
                recordId={record.id}
                updateComments={updateComments}
            />
        </Box>
    );
};